import { z } from 'zod';
import { Subject } from '../common/enums';
import type { TransformerConfig } from '../common/types';
import { mergeMissedMarks } from '../legacyExamAttempts/utils';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { LegacyStudentTopicAnalyticsAttributes } from './constants';
import {
  DBLegacyStudentTopicAnalyticsSchema,
  LegacyStudentTopicAnalyticsSchema,
  LegacyStudentTopicAnalyticsTopicExamsSchema,
} from './schemas';

type TopicExam = z.infer<typeof LegacyStudentTopicAnalyticsTopicExamsSchema>[number];

/**
 * Codec configuration for LegacyStudentTopicAnalytics entity.
 *
 * Transformations:
 * - Client `subject` + `topicCode` ↔ DB `subjectTopicIdentifier` (format: "TOPIC#{subject}#{topicCode}")
 * - Client `topicExams` (array) ↔ DB `topicExams` (record keyed by exam code)
 */
export const LegacyStudentTopicAnalyticsCodecConfig: TransformerConfig<
  typeof LegacyStudentTopicAnalyticsSchema,
  typeof DBLegacyStudentTopicAnalyticsSchema
> = {
  name: 'LegacyStudentTopicAnalytics',
  clientSchema: LegacyStudentTopicAnalyticsSchema,
  dbSchema: DBLegacyStudentTopicAnalyticsSchema,
  transformers: [
    // studentId ↔ student
    createTransformer<
      typeof LegacyStudentTopicAnalyticsSchema,
      typeof DBLegacyStudentTopicAnalyticsSchema
    >({
      encoder: {
        sourceFields: ['studentId'] as const,
        encode: ({ studentId }) => ({
          student: studentId,
        }),
      },
      decoder: {
        sourceFields: ['student'] as const,
        decode: ({ student }) => ({
          studentId: student,
        }),
      },
    }),

    // subject + topicCode ↔ subjectTopicIdentifier: "TOPIC#{subject}#{topicCode}"
    createTransformer<
      typeof LegacyStudentTopicAnalyticsSchema,
      typeof DBLegacyStudentTopicAnalyticsSchema
    >({
      encoder: {
        sourceFields: ['subject', 'topicCode'] as const,
        encode: ({ subject, topicCode }) => ({
          subjectTopicIdentifier: `TOPIC#${subject}#${topicCode}`,
        }),
      },
      decoder: {
        sourceFields: ['subjectTopicIdentifier'] as const,
        decode: ({ subjectTopicIdentifier }) => {
          const parts = subjectTopicIdentifier.split('#');
          return {
            subject: parts[1] as Subject,
            topicCode: parts[2]!,
          };
        },
      },
    }),

    // topicExams: [{ label, type, examCode }] ↔ topicExams: { examCode: { label, type, value: examCode } }
    createTransformer<
      typeof LegacyStudentTopicAnalyticsSchema,
      typeof DBLegacyStudentTopicAnalyticsSchema
    >({
      encoder: {
        sourceFields: ['topicExams'] as const,
        encode: ({ topicExams }) => ({
          topicExams: Object.fromEntries(
            topicExams.map((exam: TopicExam) => [
              exam.examCode,
              { label: exam.label, type: exam.type, value: exam.examCode },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['topicExams'] as const,
        decode: ({ topicExams }) => ({
          topicExams: Object.entries(topicExams ?? {}).map(([examCode, examData]) => ({
            label: examData.label,
            type: examData.type,
            examCode,
          })),
        }),
      },
    }),

    // Merge missedMarks into inaccuracyMarks for each analytics sub-object on decode
    createTransformer<
      typeof LegacyStudentTopicAnalyticsSchema,
      typeof DBLegacyStudentTopicAnalyticsSchema
    >({
      decoder: {
        sourceFields: LegacyStudentTopicAnalyticsAttributes.Analytics,
        decode: data => {
          const result: Record<string, unknown> = {};
          const record = data as Record<
            string,
            { inaccuracyMarks?: number; missedMarks?: number } | undefined
          >;
          for (const key of LegacyStudentTopicAnalyticsAttributes.Analytics) {
            if (record[key]) {
              result[key] = mergeMissedMarks(record[key]);
            }
          }
          return result;
        },
      },
    }),
  ],
};

export const encodeLegacyStudentTopicAnalyticsToDb = createEncodeToDbFn(
  LegacyStudentTopicAnalyticsCodecConfig
);
export const decodeLegacyStudentTopicAnalyticsFromDb = createDecodeFromDbFn(
  LegacyStudentTopicAnalyticsCodecConfig
);
